import _extends from "@babel/runtime/helpers/esm/extends";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import _asyncToGenerator from "@babel/runtime/helpers/esm/asyncToGenerator";
import React from 'react';
import invariant from 'tiny-invariant';
import useCancellablePromise from '@sprinx/react-hooks/useCancellablePromise';

var existsOrCb = function existsOrCb(cb) {
  return function (v) {
    return v !== undefined && v !== null ? v : cb();
  };
};

var dummyCache = {
  getItem: function getItem(key) {
    return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              return _context.abrupt("return", localStorage ? localStorage.getItem(key) : null);

            case 1:
            case "end":
              return _context.stop();
          }
        }
      }, _callee);
    }))();
  },
  setItem: function setItem(key, value) {
    return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2() {
      return _regeneratorRuntime.wrap(function _callee2$(_context2) {
        while (1) {
          switch (_context2.prev = _context2.next) {
            case 0:
              return _context2.abrupt("return", localStorage ? localStorage.setItem(key, value) : undefined);

            case 1:
            case "end":
              return _context2.stop();
          }
        }
      }, _callee2);
    }))();
  }
};
export default function useLocaleManager(KEY, dontResolveLocale, defaultLocale, settingsApiCall, parentCache) {
  invariant(KEY, 'useLocaleManager(...): KEY is required.');
  invariant(defaultLocale && typeof defaultLocale === 'string' && defaultLocale.length >= 2, "useLocaleManager(...): 'defaultLocale' have to be 'string' at least two chars long.");

  var _React$useState = React.useState(function () {
    return dontResolveLocale ? {
      loading: false,
      error: null,
      preferredLocale: defaultLocale
    } : {
      loading: true,
      error: null,
      preferredLocale: null
    };
  }),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      _React$useState2$ = _React$useState2[0],
      error = _React$useState2$.error,
      loading = _React$useState2$.loading,
      preferredLocale = _React$useState2$.preferredLocale,
      setState = _React$useState2[1];

  var cancellablePromise = useCancellablePromise();
  var apiTimeoutRef = React.useRef();
  var cache = React.useMemo(function () {
    return parentCache || dummyCache;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  var setPreferredLocaleInner = React.useCallback(function (nextLoading, nextError, nextPreferredLocale) {
    if (nextPreferredLocale) {
      setState(function (p) {
        return _extends({}, p, {
          preferredLocale: nextPreferredLocale,
          loading: !!nextLoading,
          error: nextError
        });
      });
      cache.setItem(KEY, nextPreferredLocale);
    }
  }, [KEY, cache]);
  var setPreferredLocale = React.useCallback(function (nextPreferredLocale) {
    return setPreferredLocaleInner(false, null, nextPreferredLocale);
  }, [setPreferredLocaleInner]);
  var callApi = React.useCallback( /*#__PURE__*/function () {
    var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3(k) {
      var pr;
      return _regeneratorRuntime.wrap(function _callee3$(_context3) {
        while (1) {
          switch (_context3.prev = _context3.next) {
            case 0:
              pr = function pr(cb, t) {
                return function (x) {
                  clearTimeout(t);
                  cb(x);
                };
              };

              return _context3.abrupt("return", !settingsApiCall ? undefined : new Promise(function (resolve, reject) {
                var inner = cancellablePromise(settingsApiCall(k));
                inner.then(pr(resolve, apiTimeoutRef.current), pr(reject, apiTimeoutRef.current));
                apiTimeoutRef.current = setTimeout(function () {
                  inner.cancel();
                  resolve();
                }, 5000);
                return inner;
              }));

            case 2:
            case "end":
              return _context3.stop();
          }
        }
      }, _callee3);
    }));

    return function (_x) {
      return _ref.apply(this, arguments);
    };
  }(), [cancellablePromise, settingsApiCall]);
  React.useEffect(function () {
    if (!dontResolveLocale) {
      cancellablePromise(cache.getItem(KEY)).then(existsOrCb(function () {
        return callApi(KEY);
      }), function () {
        return callApi(KEY);
      }).then(existsOrCb(function () {
        return defaultLocale;
      }), function (er) {
        return [er, defaultLocale];
      }).then(function (l) {
        return Array.isArray(l) ? function (_ref2) {
          var _ref3 = _slicedToArray(_ref2, 2),
              er = _ref3[0],
              lc = _ref3[1];

          // eslint-disable-next-line no-console
          console.error('Resolve locale error:', er); // istanbul ignore next

          setPreferredLocaleInner(false, null, lc || defaultLocale);
        }(l) : setPreferredLocaleInner(false, null, l);
      });
    }

    return function () {
      // istanbul ignore next
      if (apiTimeoutRef.current) {
        clearTimeout(apiTimeoutRef.current);
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return [preferredLocale, error, loading, setPreferredLocale];
}